import { Link } from "react-router-dom";
import { MapPin, Phone, Instagram, Linkedin, Facebook, Cookie } from "lucide-react";
import { Logo } from "@/components/site/Navbar";

const NAV = [
  { label: "Leistungen", href: "/#leistungen" },
  { label: "Konzept & Vorgehen", href: "/#konzept" },
  { label: "Über Uns", href: "/#ueber-uns" },
  { label: "Kontakt", href: "/#kontakt" },
];

const SERVICES = [
  "Webdesign Lüdenscheid",
  "Homepage erstellen",
  "Logo Design Hagen",
  "Local SEO Märkischer Kreis",
];

const SOCIALS = [
  { icon: Instagram, label: "Instagram", href: "https://instagram.com" },
  { icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com" },
  { icon: Facebook, label: "Facebook", href: "https://facebook.com" },
];

export const Footer = () => {
  const openCookieSettings = (e) => {
    e.preventDefault();
    window.dispatchEvent(new Event("open-cookie-settings"));
  };

  return (
    <footer data-testid="footer" className="border-t border-white/10 bg-[#0A0A0A]">
      {/* Main footer content — extra bottom padding on mobile to clear sticky CTA bar */}
      <div className="mx-auto max-w-7xl px-5 pt-16 pb-32 sm:px-8 lg:pt-20 lg:pb-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Logo />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-muted-foreground">
              Wango UG (haftungsbeschränkt) – Ihre Digitalagentur für Webdesign, Branding & Marketing im Märkischen Kreis.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-[#121212] text-muted-foreground transition-colors duration-300 hover:border-[#C5A059] hover:text-[#C5A059]"
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <nav aria-label="Footer Navigation">
            <h3 className="font-heading text-xs font-semibold uppercase tracking-wider text-[#C5A059]">
              Navigation
            </h3>
            <ul className="mt-5 space-y-2">
              {NAV.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="inline-flex min-h-[44px] items-center text-sm text-muted-foreground transition-colors duration-200 hover:text-white"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Services keywords */}
          <div>
            <h3 className="font-heading text-xs font-semibold uppercase tracking-wider text-[#C5A059]">
              Leistungen
            </h3>
            <ul className="mt-5 space-y-2">
              {SERVICES.map((service) => (
                <li key={service}>
                  <a
                    href="/#leistungen"
                    className="inline-flex min-h-[44px] items-center text-sm text-muted-foreground transition-colors duration-200 hover:text-white"
                  >
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading text-xs font-semibold uppercase tracking-wider text-[#C5A059]">
              Kontakt
            </h3>
            <div className="mt-5 flex items-center gap-3 text-sm text-muted-foreground">
              <Phone className="h-4 w-4 shrink-0 text-[#C5A059]" />
              <a href="tel:+491748367114" className="hover:text-[#C5A059] transition-colors duration-200">
                +49 174 8367114
              </a>
            </div>
            <div className="mt-3 text-sm text-muted-foreground">
              <a
                href="mailto:info@wango-media.com"
                className="hover:text-[#C5A059] transition-colors duration-200"
              >
                info@wango-media.com
              </a>
            </div>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Mo – Fr, 9:00 – 18:00 Uhr
              <br />
              Lüdenscheid, NRW
            </p>
          </div>
        </div>

        {/* Bottom bar — clean, minimal, touch-friendly */}
        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Wango Media
          </p>
          <div className="flex flex-wrap items-center gap-1">
            <Link
              to="/impressum"
              data-testid="footer-impressum"
              className="inline-flex min-h-[44px] items-center px-3 py-2 text-xs text-muted-foreground transition-colors duration-200 hover:text-[#C5A059]"
            >
              Impressum
            </Link>
            <span className="text-white/20 text-xs">·</span>
            <Link
              to="/datenschutz"
              data-testid="footer-datenschutz"
              className="inline-flex min-h-[44px] items-center px-3 py-2 text-xs text-muted-foreground transition-colors duration-200 hover:text-[#C5A059]"
            >
              Datenschutz
            </Link>
            <span className="text-white/20 text-xs">·</span>
            <button
              type="button"
              onClick={openCookieSettings}
              data-testid="footer-cookie-settings"
              className="inline-flex min-h-[44px] items-center gap-1 px-3 py-2 text-xs text-muted-foreground transition-colors duration-200 hover:text-[#C5A059]"
            >
              <Cookie className="h-3.5 w-3.5" />
              Cookie-Einstellungen
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
